import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui";
import { getErrorMessage } from "@/lib/strapi";
import { ROUTES } from "@/routes/paths";
import { SECTIONS, SECTION_LABELS } from "@/config/constants";
import {
  useCreateOrder,
  CheckoutFields,
  checkoutSchema,
  checkoutInitialValues,
} from "@/features/orders";

const schema = checkoutSchema.shape({
  product_url: Yup.string().trim().url("Enter a full URL, e.g. https://…").required("Required"),
  product_title: Yup.string().trim().max(200, "Too long"),
  section: Yup.string().oneOf([SECTIONS.PK_FASHION, SECTIONS.NA_PRODUCTS]).required("Required"),
});

const inputClass =
  "w-full rounded-md border border-input bg-background p-2 text-foreground focus:border-ring focus:ring-1 focus:ring-ring";

/**
 * "Send us a link" — a normal order whose product is a URL rather than a
 * catalog item, so it goes through the orders endpoint.
 */
export function LinkRequestForm() {
  const navigate = useNavigate();
  const create = useCreateOrder({
    onSuccess: (order) => {
      toast.success(`Request ${order.order_number} sent — check your email.`);
      navigate(ROUTES.TRACK(order.access_token));
    },
    onError: (error) => toast.error(getErrorMessage(error, "Could not send request")),
  });

  return (
    <Formik
      initialValues={{
        ...checkoutInitialValues,
        product_url: "",
        product_title: "",
        section: SECTIONS.PK_FASHION,
      }}
      validationSchema={schema}
      onSubmit={(values) => create.mutate(values)}
    >
      <Form className="space-y-5">
        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-foreground">What you want</legend>
          <div>
            <label htmlFor="product_url" className="mb-1 block text-sm text-foreground">
              Product link <span className="text-destructive">*</span>
            </label>
            <Field
              id="product_url"
              name="product_url"
              type="url"
              placeholder="https://www.sanasafinaz.com/…"
              className={inputClass}
            />
            <ErrorMessage name="product_url" component="p" className="mt-1 text-xs text-destructive" />
          </div>
          <div>
            <label htmlFor="product_title" className="mb-1 block text-sm text-foreground">
              What is it?
            </label>
            <Field
              id="product_title"
              name="product_title"
              placeholder="Embroidered lawn suit, size M"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="section" className="mb-1 block text-sm text-foreground">
              Section
            </label>
            <Field id="section" name="section" as="select" className={inputClass}>
              <option value={SECTIONS.PK_FASHION}>{SECTION_LABELS[SECTIONS.PK_FASHION]}</option>
              <option value={SECTIONS.NA_PRODUCTS}>{SECTION_LABELS[SECTIONS.NA_PRODUCTS]}</option>
            </Field>
          </div>
        </fieldset>

        <CheckoutFields />

        <Button type="submit" className="w-full" isLoading={create.isPending}>
          Send request
        </Button>
      </Form>
    </Formik>
  );
}

export default LinkRequestForm;
